export interface SeoData {
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string;
  ogType: string;
}

const rawSiteUrl =
  (import.meta.env as Record<string, string | undefined>).VITE_SITE_URL || "";

export const SITE_URL = (
  rawSiteUrl ||
  (typeof window !== "undefined" ? window.location.origin : "")
).replace(/\/+$/, "");

export const DEFAULT_SEO: SeoData = {
  title: "ACOMA | IA, Software e Integraciones ERP en Chile",
  description:
    "ACOMA construye tecnología que trabaja para tu negocio: agentes de IA, desarrollo de software, integraciones SAP Business One y ERP, automatización empresarial y productos digitales en Chile.",
  keywords:
    "desarrollo de software Chile, inteligencia artificial empresas Chile, agentes IA, integración SAP Business One, integración ERP, desarrollo SaaS, automatización empresarial",
  canonicalPath: "/",
  ogType: "website",
};

export const routeSeo: Record<string, SeoData> = {
  "/": DEFAULT_SEO,
  "/inteligencia-artificial": {
    title: "Inteligencia Artificial y Agentes IA para Empresas | ACOMA",
    description:
      "Diseñamos agentes de inteligencia artificial e IA generativa que se integran con los procesos y sistemas de tu empresa para automatizar tareas, asistir equipos y ejecutar acciones reales.",
    keywords:
      "inteligencia artificial Chile, agentes IA empresas, automatización con IA, RAG, IA para ventas, IA para soporte",
    canonicalPath: "/inteligencia-artificial",
    ogType: "website",
  },
  "/agentes-ia": {
    title: "Agentes de IA Empresariales | ACOMA Agents",
    description:
      "Agentes de IA que se conectan con las herramientas de tu empresa para consultar información, automatizar tareas, asistir equipos y ejecutar acciones dentro de procesos reales.",
    keywords:
      "agentes IA Chile, agente comercial IA, agente ERP, agente soporte IA, automatización inteligente",
    canonicalPath: "/agentes-ia",
    ogType: "website",
  },
  "/desarrollo-software": {
    title: "Desarrollo de Software a Medida | ACOMA",
    description:
      "Construimos plataformas SaaS, aplicaciones web y móviles, portales administrativos y modernizamos sistemas legacy con arquitecturas escalables preparadas para crecer.",
    keywords:
      "desarrollo de software Chile, plataformas SaaS, aplicaciones web, aplicaciones móviles, modernización de sistemas, APIs",
    canonicalPath: "/desarrollo-software",
    ogType: "website",
  },
  "/integraciones": {
    title: "Integraciones de Sistemas y ERP | ACOMA",
    description:
      "Conectamos ERP, plataformas, APIs, medios de pago y servicios externos para eliminar procesos manuales y crear ecosistemas tecnológicos integrados.",
    keywords:
      "integración ERP, integración SAP Business One, integración APIs, Webhooks, integración Transbank, Mercado Pago, Stripe",
    canonicalPath: "/integraciones",
    ogType: "website",
  },
  "/sap-business-one": {
    title: "Integración SAP Business One | ACOMA",
    description:
      "Expertos en integración SAP Business One: Service Layer, DI API, SAP HANA, SQL Server, AddOns, automatización e integraciones B2B para empresas en Chile.",
    keywords:
      "SAP Business One Chile, Service Layer, DI API, SAP HANA, integración SAP B1, AddOns SAP",
    canonicalPath: "/sap-business-one",
    ogType: "website",
  },
  "/arquitectura": {
    title: "Arquitectura de Software y Cloud | ACOMA",
    description:
      "Diseñamos arquitecturas de software modernas, escalables y mantenibles para productos digitales y sistemas empresariales, con cloud, Docker, APIs y seguridad.",
    keywords:
      "arquitectura de software, SaaS multi-tenant, Docker, cloud, infraestructura, diseño de APIs, observabilidad",
    canonicalPath: "/arquitectura",
    ogType: "website",
  },
  "/soluciones": {
    title: "Soluciones Tecnológicas para Empresas | ACOMA",
    description:
      "Soluciones integrales de tecnología: IA, software, integraciones y arquitectura cloud para transformar procesos y construir productos digitales en Chile.",
    keywords:
      "soluciones tecnológicas Chile, transformación digital, socio tecnológico, IA, software, integraciones",
    canonicalPath: "/soluciones",
    ogType: "website",
  },
  "/proyectos": {
    title: "Proyectos y Productos Digitales | ACOMA",
    description:
      "Productos digitales propios y soluciones empresariales de ACOMA: PriviRooms, Contoo y APR-Online, construidos con foco en escalabilidad, pagos y usuarios.",
    keywords:
      "PriviRooms, APR-Online, productos digitales Chile, plataformas digitales, desarrollo de producto",
    canonicalPath: "/proyectos",
    ogType: "website",
  },
  "/nosotros": {
    title: "Nosotros | ACOMA — Socio Tecnológico en Chile",
    description:
      "ACOMA es una empresa tecnológica chilena que combina inteligencia artificial, software e integraciones para resolver problemas de negocio completos mediante tecnología.",
    keywords:
      "ACOMA, empresa tecnológica Chile, socio tecnológico, inteligencia artificial, integraciones ERP",
    canonicalPath: "/nosotros",
    ogType: "website",
  },
  "/contacto": {
    title: "Contacto | ACOMA",
    description:
      "Cuéntanos tu proyecto. En ACOMA te asesoramos en inteligencia artificial, desarrollo de software e integraciones ERP para llevar tu idea a producción.",
    keywords:
      "contacto ACOMA, hablemos de tu proyecto, desarrollo de software Chile, agentes IA, integración SAP",
    canonicalPath: "/contacto",
    ogType: "website",
  },
  "/privacidad": {
    title: "Política de Privacidad | ACOMA",
    description:
      "Política de privacidad de ACOMA: cómo recopilamos, usamos y protegemos la información personal de los visitantes y clientes de nuestro sitio.",
    keywords: "política de privacidad, ACOMA, protección de datos",
    canonicalPath: "/privacidad",
    ogType: "website",
  },
  "/terminos": {
    title: "Términos y Condiciones | ACOMA",
    description:
      "Términos y condiciones de uso del sitio web de ACOMA, empresa tecnológica chilena especializada en IA, software e integraciones ERP.",
    keywords: "términos y condiciones, ACOMA, uso del sitio",
    canonicalPath: "/terminos",
    ogType: "website",
  },
};

interface ServiceSchema {
  name: string;
  description: string;
}

const serviceSchemas: Record<string, ServiceSchema> = {
  "/inteligencia-artificial": {
    name: "Inteligencia Artificial y Agentes IA",
    description:
      "Agentes de IA empresariales, asistentes internos, automatización mediante IA y bases de conocimiento conectadas a los sistemas de tu organización.",
  },
  "/desarrollo-software": {
    name: "Desarrollo de Software a Medida",
    description:
      "Plataformas SaaS, aplicaciones web y móviles, portales administrativos y modernización de sistemas legacy con arquitecturas escalables.",
  },
  "/integraciones": {
    name: "Integraciones de Sistemas y ERP",
    description:
      "Conexión de ERP, plataformas, APIs, medios de pago y servicios externos para eliminar procesos manuales.",
  },
  "/sap-business-one": {
    name: "Integración SAP Business One",
    description:
      "Service Layer, DI API, SAP HANA, SQL Server, AddOns e integraciones B2B para SAP Business One.",
  },
  "/arquitectura": {
    name: "Arquitectura de Software y Cloud",
    description:
      "Arquitecturas modernas, escalables y mantenibles con Docker, cloud, APIs y seguridad para productos digitales.",
  },
};

export function getRouteSchema(pathname: string): object | object[] | null {
  const origin = SITE_URL || "https://acomalabs.cl";

  if (pathname === "/") {
    return [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "ACOMA",
        url: `${origin}/`,
        description:
          "ACOMA construye tecnología que trabaja para tu negocio: agentes de IA, desarrollo de software e integraciones ERP en Chile.",
        areaServed: "Chile",
        knowsAbout: [
          "Inteligencia Artificial",
          "Agentes IA",
          "Desarrollo de Software",
          "Integraciones ERP",
          "SAP Business One",
          "Automatización empresarial",
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "ACOMA",
        url: `${origin}/`,
        inLanguage: "es-CL",
      },
    ];
  }

  if (serviceSchemas[pathname]) {
    const service = serviceSchemas[pathname];
    return {
      "@context": "https://schema.org",
      "@type": "Service",
      name: service.name,
      description: service.description,
      provider: {
        "@type": "Organization",
        name: "ACOMA",
        url: `${origin}/`,
      },
      areaServed: "Chile",
    };
  }

  if (pathname === "/contacto") {
    return {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Contacto | ACOMA",
      url: `${origin}/contacto`,
      inLanguage: "es-CL",
    };
  }

  if (pathname === "/agentes-ia") {
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "ACOMA Agents",
      description:
        "Agentes de IA empresariales que se conectan con las herramientas de tu empresa para consultar información y automatizar tareas.",
      brand: {
        "@type": "Brand",
        name: "ACOMA",
      },
    };
  }

  return null;
}